import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Edit3,
  Trash2,
  Filter,
  Download,
  Eye,
  Users,
  GraduationCap,
  MapPin,
  Phone,
  Mail,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  X,
  Check,
  AlertCircle,
  Calendar,
  Award,
  Building,
  BookOpen,
  UserPlus,
  Settings,
  RefreshCw,
  FileText,
  TrendingUp,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

const LecturerManagement = ({ learningMode: propLearningMode }) => {
  const { user } = useAuth();

  // Use prop if available, otherwise fallback to user object
  const learningMode = propLearningMode || user?.learningMode || user?.institution?.learningMode || 'instructor_led';
  const isSelfPaced = learningMode === 'self_paced';

  const [lecturers, setLecturers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [selectedLecturers, setSelectedLecturers] = useState([]);
  const [filterConfig, setFilterConfig] = useState({
    course: '',
    campus: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedLecturer, setSelectedLecturer] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    courseId: '',
    campusId: '',
    password: '',
    unitIds: []
  });
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [lecturerAssignments, setLecturerAssignments] = useState([]);
  const [loadingLecturerDetails, setLoadingLecturerDetails] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    courseId: '',
    campusId: '',
    unitId: '',
    classDate: '',
  });
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'compact'

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    console.log('Fetching data from API...'); // Debug log

    try {
      console.log('Making requests to:', {
        lecturers: `${API_BASE_URL}/lecturers`,
        courses: `${API_BASE_URL}/courses`,
        campuses: `${API_BASE_URL}/campuses`,
        units: `${API_BASE_URL}/units`
      }); // Debug log

      const [lecturersRes, coursesRes, campusesRes, unitsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/lecturers`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/courses`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/campuses`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/units`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      console.log('Response statuses:', {
        lecturers: lecturersRes.status,
        courses: coursesRes.status,
        campuses: campusesRes.status,
        units: unitsRes.status
      }); // Debug log

      if (lecturersRes.ok) {
        const lecturersData = await lecturersRes.json();
        console.log('Raw lecturers data:', lecturersData); // Debug log

        // Handle both array and object responses
        const lecturersArray = Array.isArray(lecturersData) ? lecturersData : [];
        console.log('Lecturers array length:', lecturersArray.length); // Debug log

        // Enhance lecturer data with assignments and statistics
        const enhancedLecturers = lecturersArray.map(lecturer => ({
          ...lecturer,
          fullName: `${lecturer.firstName || lecturer.name || ''} ${lecturer.lastName || ''}`.trim(),
          assignedUnits: Math.floor(Math.random() * 5) + 1, // Placeholder
          activeStudents: Math.floor(Math.random() * 50) + 10, // Placeholder
          performance: Math.floor(Math.random() * 30) + 70, // Placeholder
          joinDate: lecturer.createdAt || '2024-01-15', // Use actual creation date if available
          lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(), // Placeholder
          status: lecturer.status || (Math.random() > 0.3 ? 'active' : 'inactive')
        }));

        console.log('Enhanced lecturers data:', enhancedLecturers); // Debug log
        setLecturers(enhancedLecturers);
      } else {
        console.error('Failed to fetch lecturers:', lecturersRes.status, lecturersRes.statusText);
        if (lecturersRes.status === 404) {
          showNotification('Lecturers endpoint not found. Please check the API configuration.', 'error');
        } else {
          showNotification(`Failed to fetch lecturers: ${lecturersRes.status}`, 'error');
        }
      }

      if (coursesRes.ok) {
        const coursesData = await coursesRes.json();
        setCourses(Array.isArray(coursesData) ? coursesData : []);
      } else {
        console.error('Failed to fetch courses:', coursesRes.status);
      }

      if (campusesRes.ok) {
        const campusesData = await campusesRes.json();
        setCampuses(Array.isArray(campusesData) ? campusesData : []);
      } else {
        console.error('Failed to fetch campuses:', campusesRes.status);
      }

      if (unitsRes.ok) {
        const unitsData = await unitsRes.json();
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } else {
        console.error('Failed to fetch units:', unitsRes.status);
      }
    } catch (error) {
      console.error('Error fetching data:', error); // Debug log
      showNotification(`Error fetching data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Enhanced filtering and sorting logic
  const filteredAndSortedLecturers = useMemo(() => {
    let filtered = lecturers.filter(lecturer => {
      const searchStr = `${lecturer.fullName} ${lecturer.email}`.toLowerCase();
      const matchesSearch = searchStr.includes(searchTerm.toLowerCase());
      const matchesCourse = !filterConfig.course || lecturer.courseId === parseInt(filterConfig.course);
      const matchesCampus = !filterConfig.campus || lecturer.campusId === parseInt(filterConfig.campus);
      const matchesStatus = !filterConfig.status || lecturer.status === filterConfig.status;

      return matchesSearch && matchesCourse && matchesCampus && matchesStatus;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [lecturers, searchTerm, filterConfig, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedLecturers.length / itemsPerPage);
  const paginatedLecturers = filteredAndSortedLecturers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectLecturer = (lecturerId, checked) => {
    if (checked) {
      setSelectedLecturers(prev => [...prev, lecturerId]);
    } else {
      setSelectedLecturers(prev => prev.filter(id => id !== lecturerId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLecturers(paginatedLecturers.map(l => l.id));
    } else {
      setSelectedLecturers([]);
    }
  };

  const openModal = (type, lecturer = null) => {
    setModalType(type);
    setSelectedLecturer(lecturer);

    if (type === 'create') {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        courseId: '',
        campusId: '',
        password: ''
      });
    } else if (lecturer) {
      setFormData({
        firstName: lecturer.firstName || '',
        lastName: lecturer.lastName || '',
        email: lecturer.email || '',
        phoneNumber: lecturer.phoneNumber || '',
        courseId: lecturer.courseId || '',
        campusId: lecturer.campusId || '',
        password: ''
      });
    }

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const url = modalType === 'edit'
        ? `${API_BASE_URL}/lecturers/${selectedLecturer.id}`
        : `${API_BASE_URL}/lecturers`;

      const method = modalType === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          name: `${formData.firstName} ${formData.lastName}`.trim(), // Combine firstName and lastName into name
        }),
      });

      if (response.ok) {
        showNotification(
          `Lecturer ${modalType === 'edit' ? 'updated' : 'created'} successfully!`,
          'success'
        );
        setShowModal(false);
        fetchAllData();
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Failed to save lecturer', 'error');
      }
    } catch (error) {
      showNotification('Error saving lecturer', 'error');
    }
  };

  const handleDelete = async (lecturerId) => {
    if (!window.confirm('Are you sure you want to delete this lecturer?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${lecturerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Lecturer deleted successfully!', 'success');
        fetchAllData();
      } else {
        showNotification('Failed to delete lecturer', 'error');
      }
    } catch (error) {
      showNotification('Error deleting lecturer', 'error');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedLecturers.length === 0) return;

    const confirmMessage = action === 'delete'
      ? `Are you sure you want to delete ${selectedLecturers.length} lecturers?`
      : `Are you sure you want to ${action} ${selectedLecturers.length} lecturers?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const promises = selectedLecturers.map(id =>
        fetch(`${API_BASE_URL}/lecturers/${id}`, {
          method: action === 'delete' ? 'DELETE' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: action !== 'delete' ? JSON.stringify({ status: action }) : undefined
        })
      );

      await Promise.all(promises);
      showNotification(`Bulk ${action} completed successfully!`, 'success');
      setSelectedLecturers([]);
      fetchAllData();
    } catch (error) {
      showNotification(`Error performing bulk ${action}`, 'error');
    }
  };

  const getLecturerDetails = (lecturer) => {
    const course = courses.find(c => c.id === lecturer.courseId);
    const campus = campuses.find(c => c.id === lecturer.campusId);
    return {
      courseName: course?.name || 'Not Assigned',
      campusName: campus?.name || 'Not Assigned'
    };
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: Check },
      inactive: { bg: 'bg-red-100', text: 'text-red-800', icon: X },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertCircle }
    };

    const { bg, text, icon: Icon } = config[status] || config.inactive;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const openAssignmentModal = async (lecturer) => {
    setSelectedLecturer(lecturer);
    setLoadingAssignments(true);
    setShowAssignmentModal(true);

    try {
      // Fetch all assigned units for this lecturer
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${lecturer.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let assignedUnits = [];

      if (response.ok) {
        const data = await response.json();
        console.log('Raw assigned units data:', data); // Debug log
        assignedUnits = Array.isArray(data) ? data : [];
      }

      // Sort by creation date (newest first)
      assignedUnits.sort((a, b) => new Date(b.createdAt || b.updatedAt) - new Date(a.createdAt || a.updatedAt));

      setAssignedUnits(assignedUnits);
    } catch (error) {
      console.error('Error fetching assigned units:', error);
      setAssignedUnits([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if this is a reassignment (unitId is pre-filled)
      const isReassignment = assignmentData.unitId && assignedUnits.some(unit => unit.id === parseInt(assignmentData.unitId));

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${selectedLecturer.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          courseId: assignmentData.courseId,
          campusId: assignmentData.campusId,
          unitId: assignmentData.unitId,
          classDate: assignmentData.classDate,
        }),
      });

      if (response.ok) {
        const successMessage = isReassignment
          ? 'Lecturer reassigned successfully! Assignment details updated.'
          : 'Lecturer assigned successfully! Unit and student records updated.';
        showNotification(successMessage, 'success');

        // Clear the form and refresh data
        setAssignmentData({
          campusId: '',
          unitId: '',
          classDate: '',
        });

        // Refresh assigned units and main data
        await refreshAssignedUnits();
        fetchAllData();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || 'Failed to assign lecturer', 'error');
      }
    } catch (error) {
      console.error('Error assigning lecturer:', error);
      showNotification('Error assigning lecturer', 'error');
    }
  };

  const handleUnassign = async (unitId) => {
    if (!window.confirm('Are you sure you want to unassign this lecturer from this unit? This will remove the lecturer assignment and reset the unit status.')) return;

    try {
      setLoadingAssignments(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${selectedLecturer.id}/unassign/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Lecturer unassigned successfully! Unit status reset to upcoming.', 'success');
        // Refresh the assigned units
        await refreshAssignedUnits();
        // Also refresh main data to update lecturer stats
        fetchAllData();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || 'Failed to unassign lecturer', 'error');
      }
    } catch (error) {
      console.error('Error unassigning lecturer:', error);
      showNotification('Error unassigning lecturer', 'error');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleAcceptAssignment = async (unitId) => {
    if (!window.confirm('Are you sure you want to accept this assignment? This will activate the unit for teaching.')) return;

    try {
      setLoadingAssignments(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${selectedLecturer.id}/units/${unitId}/accept-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        showNotification('Assignment accepted successfully! Unit is now ongoing.', 'success');
        // Refresh the assigned units
        await refreshAssignedUnits();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || 'Failed to accept assignment', 'error');
      }
    } catch (error) {
      console.error('Error accepting assignment:', error);
      showNotification('Error accepting assignment', 'error');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleCompleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to mark this unit as completed? This action cannot be undone.')) return;

    try {
      setLoadingAssignments(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${selectedLecturer.id}/units/${unitId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        showNotification('Unit marked as completed successfully!', 'success');
        // Refresh the assigned units
        await refreshAssignedUnits();
      } else {
        const errorData = await response.json();
        showNotification(errorData.message || 'Failed to complete unit', 'error');
      }
    } catch (error) {
      console.error('Error completing unit:', error);
      showNotification('Error completing unit', 'error');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const AssignmentStatusDisplay = ({ status, declineReason = null, declinedAt = null }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const statusConfig = {
      pending: {
        label: 'Waiting for lecturer acceptance...',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        icon: AlertCircle
      },
      accepted: {
        label: 'Ongoing',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        icon: Activity
      },
      pending_acceptance: {
        label: 'Waiting for lecturer acceptance...',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        icon: AlertCircle
      },
      active: {
        label: 'Ongoing',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        icon: Activity
      },
      completed: {
        label: 'Completed',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        icon: Check
      },
      upcoming: {
        label: 'Upcoming',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        icon: Calendar
      },
      declined: {
        label: 'Declined',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        icon: X
      }
    };

    const config = statusConfig[status] || statusConfig.upcoming;
    const IconComponent = config.icon;

    return (
      <div className="relative">
        {status === 'declined' ? (
          <button
            onClick={() => setShowTooltip(!showTooltip)}
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color} hover:opacity-80 transition-opacity cursor-pointer`}
            title="Click to show decline reason"
          >
            <IconComponent className="w-3 h-3 mr-1" />
            {config.label}
          </button>
        ) : (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
            <IconComponent className="w-3 h-3 mr-1" />
            {config.label}
          </span>
        )}

        {status === 'declined' && showTooltip && declineReason && (
          <div className="absolute z-10 mt-1 w-64 p-3 bg-white border border-gray-200 rounded-lg shadow-lg text-left">
            <div className="text-sm text-gray-900 mb-2">
              <strong>Decline Reason:</strong>
            </div>
            <div className="text-sm text-gray-700 mb-2">
              {declineReason}
            </div>
            {declinedAt && (
              <div className="text-xs text-gray-500">
                Declined on: {new Date(declinedAt).toLocaleDateString()}
              </div>
            )}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 bg-white border-l border-t border-gray-200 rotate-45"></div>
          </div>
        )}
      </div>
    );
  };

  const openLecturerDetailsModal = async (lecturer) => {
    setSelectedLecturer(lecturer);
    setLoadingLecturerDetails(true);
    setShowDetailsModal(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${lecturer.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLecturerAssignments(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch lecturer assignments:', response.status);
        setLecturerAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching lecturer assignments:', error);
      setLecturerAssignments([]);
    } finally {
      setLoadingLecturerDetails(false);
    }
  };

  const refreshAssignedUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/lecturers/${selectedLecturer.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedUnits(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error refreshing assigned units:', error);
    }
  };

  // Filter units based on selected course
  const filterUnitsByCourse = (courseId) => {
    if (!courseId) {
      setFilteredUnits([]);
      return;
    }

    const filtered = units.filter(unit => unit.courseId === parseInt(courseId));
    setFilteredUnits(filtered);
  };

  // Handle course selection change in assignment form
  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setAssignmentData(prev => ({
      ...prev,
      courseId,
      unitId: '' // Reset unit selection when course changes
    }));
    filterUnitsByCourse(courseId);
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", trend }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600 font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const SortableHeader = ({ label, sortKey, className = "" }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ?
            <ChevronUp className="w-4 h-4" /> :
            <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading lecturer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
          <div className="flex items-center space-x-2">
            {notification.type === 'success' ?
              <Check className="w-5 h-5" /> :
              <AlertCircle className="w-5 h-5" />
            }
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                <Users className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{isSelfPaced ? 'Tutor Management' : 'Lecturer Management'}</h1>
                <p className="text-blue-100 mt-2">Advanced data management for {isSelfPaced ? 'tutors and pools' : 'academic staff'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setViewMode(viewMode === 'table' ? 'compact' : 'table')}
                className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Activity className="w-4 h-4" />
                <span>{viewMode === 'table' ? 'Compact' : 'Detailed'}</span>
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <button
                onClick={() => openModal('create')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg flex items-center space-x-2 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{isSelfPaced ? 'Add Tutor' : 'Add Lecturer'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Users}
            title={isSelfPaced ? 'Total Tutors' : 'Total Lecturers'}
            value={lecturers.length}
            subtitle={isSelfPaced ? 'Active tutors' : 'Active academic staff'}
            color="blue"
            trend="+12% this month"
          />
          <StatCard
            icon={GraduationCap}
            title={isSelfPaced ? 'Assigned Tutors' : 'Assigned Lecturers'}
            value={lecturers.filter(l => l.courseId).length}
            subtitle={isSelfPaced ? 'With pool assignments' : 'With course assignments'}
            color="green"
            trend="+8% this week"
          />
          <StatCard
            icon={MapPin}
            title="Campus Distribution"
            value={campuses.length}
            subtitle="Active locations"
            color="purple"
          />
          <StatCard
            icon={AlertCircle}
            title="Performance Score"
            value={`${Math.round(lecturers.reduce((acc, l) => acc + l.performance, 0) / lecturers.length)}%`}
            subtitle="Average rating"
            color="orange"
            trend="+5% improvement"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
              <button
                onClick={() => setFilterConfig({ course: '', campus: '', status: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
                <select
                  value={filterConfig.course}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, course: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campus</label>
                <select
                  value={filterConfig.campus}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, campus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Campuses</option>
                  {campuses.map(campus => (
                    <option key={campus.id} value={campus.id}>{campus.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filterConfig.status}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Performance</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">All Ratings</option>
                  <option value="excellent">Excellent (90%+)</option>
                  <option value="good">Good (80-89%)</option>
                  <option value="average">Average (70-79%)</option>
                  <option value="below">Below Average (&lt;70%)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80"
                />
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-500">per page</span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {selectedLecturers.length > 0 && (
                <div className="flex items-center space-x-2 mr-4">
                  <span className="text-sm text-gray-600">{selectedLecturers.length} selected</span>
                  <button
                    onClick={() => setShowBulkActions(!showBulkActions)}
                    className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                  >
                    Bulk Actions
                  </button>
                </div>
              )}
              <button
                onClick={fetchAllData}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Bulk Actions Panel */}
          {showBulkActions && selectedLecturers.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  Bulk actions for {selectedLecturers.length} {isSelfPaced ? 'tutor(s)' : 'lecturer(s)'}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAction('activate')}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => handleBulkAction('deactivate')}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLecturers.length === paginatedLecturers.length && paginatedLecturers.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <SortableHeader label={isSelfPaced ? 'Tutor' : 'Lecturer'} sortKey="fullName" />
                  <SortableHeader label="Contact" sortKey="email" />
                  <SortableHeader label={isSelfPaced ? 'Subject' : 'Course'} sortKey="courseId" />
                  <SortableHeader label="Campus" sortKey="campusId" />
                  <SortableHeader label="Performance" sortKey="performance" />
                  <SortableHeader label="Status" sortKey="status" />
                  <SortableHeader label="Units" sortKey="assignedUnits" />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLecturers.map((lecturer) => {
                  const details = getLecturerDetails(lecturer);
                  return (
                    <tr key={lecturer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLecturers.includes(lecturer.id)}
                          onChange={(e) => handleSelectLecturer(lecturer.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                              {lecturer.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{lecturer.fullName}</div>
                            <div className="text-sm text-gray-500">ID: {lecturer.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          {lecturer.email}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {lecturer.phoneNumber || 'Not provided'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{details.courseName}</div>
                        <div className="text-sm text-gray-500">{lecturer.assignedUnits} units assigned</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          {details.campusName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="h-2 rounded-full bg-gradient-to-r from-green-400 to-blue-500"
                              style={{ width: `${lecturer.performance}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{lecturer.performance}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(lecturer.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-lg font-semibold text-gray-900">{lecturer.assignedUnits}</div>
                        <div className="text-xs text-gray-500">{lecturer.activeStudents} students</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openLecturerDetailsModal(lecturer)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openAssignmentModal(lecturer)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="Assign to Campus/Unit"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal('edit', lecturer)}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lecturer.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredAndSortedLecturers.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredAndSortedLecturers.length}</span> results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredAndSortedLecturers.length === 0 && !loading && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No lecturers found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || Object.values(filterConfig).some(v => v)
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first lecturer'}
            </p>
            {!searchTerm && !Object.values(filterConfig).some(v => v) && (
              <div className="mt-6">
                <button
                  onClick={() => openModal('create')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Lecturer</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalType === 'create'
                  ? (isSelfPaced ? 'Add New Tutor' : 'Add New Lecturer')
                  : (isSelfPaced ? 'Edit Tutor' : 'Edit Lecturer')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{isSelfPaced ? 'Subject Assignment' : 'Course Assignment'}</label>
                  <select
                    value={formData.courseId}
                    onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campus Assignment</label>
                  <select
                    value={formData.campusId}
                    onChange={(e) => setFormData({ ...formData, campusId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a campus</option>
                    {campuses.map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject Pool Selection (Self-Paced Tutors) */}
              {isSelfPaced && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject Pool (Tutor Pool)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-300 rounded-lg">
                    {units.map(unit => (
                      <label key={unit.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.unitIds?.includes(unit.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked
                              ? [...(formData.unitIds || []), unit.id]
                              : (formData.unitIds || []).filter(id => id !== unit.id);
                            setFormData({ ...formData, unitIds: newIds });
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 truncate">{unit.name} ({unit.code})</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 italic">Selecting subjects here will add this {isSelfPaced ? 'tutor' : 'lecturer'} to the respective pools.</p>
                </div>
              )}

              {modalType === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter password"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  {modalType === 'create'
                    ? (user?.institution?.learningMode === 'self_paced' ? 'Create Tutor' : 'Create Lecturer')
                    : (user?.institution?.learningMode === 'self_paced' ? 'Update Tutor' : 'Update Lecturer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lecturer Details Modal */}
      {showDetailsModal && selectedLecturer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold backdrop-blur-sm">
                    {selectedLecturer.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedLecturer.fullName}</h2>
                    <p className="text-blue-100">Lecturer Profile & Performance</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Personal Information */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <UserPlus className="w-5 h-5 mr-2 text-blue-600" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-900">{selectedLecturer.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium text-gray-900">{selectedLecturer.phoneNumber || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Join Date</p>
                        <p className="font-medium text-gray-900">{new Date(selectedLecturer.joinDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Active</p>
                        <p className="font-medium text-gray-900">{new Date(selectedLecturer.lastActive).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Assignment Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-green-600" />
                      Current Assignments ({lecturerAssignments.length})
                    </h3>

                    {loadingLecturerDetails ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Loading assignments...</span>
                      </div>
                    ) : lecturerAssignments.length > 0 ? (
                      <div className="space-y-3">
                        {lecturerAssignments.map((unitData, index) => {
                          const unitId = unitData.id;
                          const unitName = unitData.name;
                          const courseId = unitData.courseId;

                          // Get campus information - prefer the assignment-specific campus name
                          const campusDisplay = unitData.campusName || unitData.lecturer?.campus?.name ||
                            (unitData.campusId ? `Campus ID: ${unitData.campusId}` : 'Campus not assigned');

                          // Handle date fields from the unit object
                          const assignedDate = unitData.createdAt || unitData.updatedAt;
                          const classDate = unitData.classDate;

                          // Format dates safely
                          const formatDate = (dateValue) => {
                            if (!dateValue) return 'Not set';
                            try {
                              const date = new Date(dateValue);
                              return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
                            } catch {
                              return 'Invalid date';
                            }
                          };

                          return (
                            <div key={unitData.id || unitData._id || index} className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1">
                                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <BookOpen className="w-4 h-4 text-green-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 text-sm">
                                      {unitName || `Unit ${unitId}`}
                                    </h4>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {campusDisplay}
                                    </p>
                                    <div className="flex items-center space-x-4 mt-2">
                                      <div className="flex items-center space-x-1">
                                        <Calendar className="w-3 h-3 text-gray-400" />
                                        <span className="text-xs text-gray-500">
                                          Class: {formatDate(classDate)}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Activity className="w-3 h-3 text-gray-400" />
                                        <span className="text-xs text-gray-500">
                                          Assigned: {formatDate(assignedDate)}
                                        </span>
                                      </div>
                                    </div>
                                    {unitData.course?.name && (
                                      <p className="text-xs text-green-600 mt-1">
                                        Course: {unitData.course.name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4">
                                  <AssignmentStatusDisplay
                                    status={unitData.assignmentStatus || unitData.status}
                                    declineReason={unitData.declineReason}
                                    declinedAt={unitData.declinedAt}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-sm">No current assignments</p>
                        <p className="text-xs text-gray-400 mt-1">This lecturer has no assigned units</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Stats */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                      Performance
                    </h3>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">{selectedLecturer.performance}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                        <div
                          className="h-3 rounded-full bg-gradient-to-r from-green-400 to-blue-500"
                          style={{ width: `${selectedLecturer.performance}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600">Overall Rating</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-900">Students</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{selectedLecturer.activeStudents}</span>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <BookOpen className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-gray-900">Units</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{selectedLecturer.assignedUnits}</span>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Activity className="w-5 h-5 text-purple-600" />
                          <span className="font-medium text-gray-900">Status</span>
                        </div>
                        {getStatusBadge(selectedLecturer.status)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    openModal('edit', selectedLecturer);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center space-x-2"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedLecturer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 my-8 relative flex flex-col max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Lecturer Assignment Management</h2>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">{selectedLecturer.fullName}</h3>
              <p className="text-blue-700">Manage this lecturer's campus and unit assignments for teaching responsibilities.</p>
            </div>

            {/* Current Assignments Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-green-600" />
                Current Assignments ({assignedUnits.length})
              </h3>

              {loadingAssignments ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading assignments...</span>
                </div>
              ) : assignedUnits.length > 0 ? (
                <div className="space-y-4">
                  {assignedUnits.map((unitData, index) => {
                    console.log('Processing unit assignment:', unitData); // Debug each unit assignment

                    // The backend now returns units with lecturer and campus information included
                    const unitId = unitData.id;
                    const unitName = unitData.name;
                    const lecturerId = unitData.lecturerId;
                    const courseId = unitData.courseId;

                    // Get campus information - prefer the assignment-specific campus name
                    const campusDisplay = unitData.campusName || unitData.lecturer?.campus?.name ||
                      (unitData.campusId ? `Campus ID: ${unitData.campusId}` : 'Campus not assigned');

                    // Handle date fields from the unit object
                    const assignedDate = unitData.createdAt || unitData.updatedAt;
                    const classDate = unitData.classDate;

                    // Format dates safely
                    const formatDate = (dateValue) => {
                      if (!dateValue) return 'Not set';
                      try {
                        const date = new Date(dateValue);
                        return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
                      } catch {
                        return 'Invalid date';
                      }
                    };

                    return (
                      <div key={unitData.id || unitData._id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {unitName || `Unit ${unitId}`}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {campusDisplay} • Assigned: {formatDate(assignedDate)}
                            </p>
                            {classDate && (
                              <p className="text-sm text-blue-600">
                                Class Date: {formatDate(classDate)}
                              </p>
                            )}
                            {unitData.course?.name && (
                              <p className="text-sm text-green-600">
                                Course: {unitData.course.name}
                              </p>
                            )}
                            {unitData.lecturer?.name && (
                              <p className="text-sm text-purple-600">
                                Lecturer: {unitData.lecturer.name}
                              </p>
                            )}
                            <div className="mt-2 flex items-center justify-between">
                              <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                                <strong>Assignment Details:</strong> Unit ID: {unitId}, Lecturer ID: {lecturerId}, Course ID: {courseId}
                              </div>
                              <AssignmentStatusDisplay
                                status={unitData.assignmentStatus || unitData.status}
                                declineReason={unitData.declineReason}
                                declinedAt={unitData.declinedAt}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {/* Action buttons based on assignment status */}
                          {unitData.status === 'pending_acceptance' && (
                            <button
                              onClick={() => handleAcceptAssignment(unitData.id)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              title="Accept this assignment to start teaching"
                            >
                              Accept Assignment
                            </button>
                          )}
                          {unitData.status === 'active' && (
                            <button
                              onClick={() => handleCompleteUnit(unitData.id)}
                              className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                              title="Mark this unit as completed"
                            >
                              Mark Complete
                            </button>
                          )}
                          {unitData.status === 'completed' && (
                            <span className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded">
                              Completed
                            </span>
                          )}
                          {unitData.status === 'declined' && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  // For reassignment, use campus from lecturer relationship
                                  setAssignmentData({
                                    campusId: unitData.lecturer?.campusId || '',
                                    unitId: unitId,
                                    classDate: classDate ? new Date(classDate).toISOString().split('T')[0] : '',
                                  });
                                }}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                title="Reassign this unit to a different lecturer"
                              >
                                Reassign Lecturer
                              </button>
                              <button
                                onClick={() => {
                                  // Change date for declined unit
                                  setAssignmentData({
                                    campusId: unitData.lecturer?.campusId || '',
                                    unitId: unitId,
                                    classDate: classDate ? new Date(classDate).toISOString().split('T')[0] : '',
                                  });
                                }}
                                className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                                title="Change the class date and reassign"
                              >
                                Change Date
                              </button>
                            </div>
                          )}

                          {unitData.status !== 'declined' && (
                            <>
                              <button
                                onClick={() => {
                                  // For reassignment, use campus from lecturer relationship
                                  setAssignmentData({
                                    campusId: unitData.lecturer?.campusId || '',
                                    unitId: unitId,
                                    classDate: classDate ? new Date(classDate).toISOString().split('T')[0] : '',
                                  });
                                }}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                title={unitData.lecturer?.campusId ? `Reassign (campus pre-selected)` : `Reassign (campus selection needed)`}
                              >
                                Reassign
                              </button>
                              <button
                                onClick={() => handleUnassign(unitData.id)}
                                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                              >
                                Unassign
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No current assignments</p>
                  <p className="text-sm">Assign this lecturer to units below</p>
                </div>
              )}
            </div>

            {/* New Assignment Form */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserPlus className="w-5 h-5 mr-2 text-blue-600" />
                {assignmentData.unitId ? 'Reassign Lecturer' : 'Assign to New Unit'}
              </h3>

              <form onSubmit={handleAssignmentSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
                    <select
                      value={assignmentData.courseId}
                      onChange={handleCourseChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a course</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Campus</label>
                    <select
                      value={assignmentData.campusId}
                      onChange={(e) => setAssignmentData({ ...assignmentData, campusId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a campus</option>
                      {campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                    <select
                      value={assignmentData.unitId}
                      onChange={(e) => setAssignmentData({ ...assignmentData, unitId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">
                        {assignmentData.courseId ? 'Select a unit' : 'Please select a course first'}
                      </option>
                      {(assignmentData.courseId ? filteredUnits : []).map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class Date</label>
                  <input
                    type="date"
                    value={assignmentData.classDate}
                    onChange={(e) => setAssignmentData({ ...assignmentData, classDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setAssignmentData({
                        courseId: '',
                        campusId: '',
                        unitId: '',
                        classDate: '',
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    Clear Form
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAssignmentModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center space-x-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>{assignmentData.unitId ? 'Reassign Lecturer' : 'Assign Lecturer'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LecturerManagement;
